import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { paymentsService } from '../../services/paymentsService';
import { authService } from '../../services/authService';
import { SingleCardLogo } from '../Common/CreditCardLogos';
import { detectCardType } from '../../utils/cardDetection';
import './SecurePaymentDetailsModal.css';

const SecurePaymentDetailsModal = ({ payment, onClose }) => {
  const { user } = useAuth();
  const [step, setStep] = useState('verify'); // 'verify', 'code', 'details'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [tempToken, setTempToken] = useState('');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    let timer;
    if (step === 'details' && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            setSessionExpired(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [step, timeLeft]);

  const handleRequestVerification = async () => {
    try {
      setLoading(true);
      setError('');

      // Request 2FA code for payment access. The response's tempToken must
      // be captured and sent back with the code on verify - without it,
      // verify-access has nothing to check the code against.
      const { tempToken: token } = await authService.requestPaymentAccess(payment.id);
      setTempToken(token);
      setStep('code');
    } catch (error) {
      console.error('Error requesting verification:', error);
      setError(error.response?.data?.error || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Verify the code against the temp token from request-access, and use
      // its response directly - verify-access already returns the fully
      // decrypted payment details in one call (see backend/routes/payments.js
      // POST /:id/verify-access). There's no separate "fetch details" step:
      // GET /:id/details doesn't check the code at all and never decrypts
      // card data, so it must not be used here.
      const details = await paymentsService.verifyPaymentAccess(payment.id, tempToken, verificationCode);
      setPaymentDetails(details);
      setStep('details');
      setTimeLeft(600); // 10 minutes for viewing
    } catch (error) {
      console.error('Error verifying code:', error);
      setError(error.response?.data?.error || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getBillingAddress = (billingData) => {
    if (!billingData) return 'N/A';
    try {
      const parsed = typeof billingData === 'string' ? JSON.parse(billingData) : billingData;
      return `${parsed.address || ''}, ${parsed.city || ''}, ${parsed.state || ''} ${parsed.zip || ''}`.trim();
    } catch {
      return 'N/A';
    }
  };

  const formatCardNumber = (cardNumber) => {
    if (!cardNumber) return 'N/A';
    // Format as XXXX XXXX XXXX XXXX
    return cardNumber.replace(/(.{4})/g, '$1 ').trim();
  };

  const getPaymentData = () => {
    if (!paymentDetails?.payment_data) return null;
    try {
      return typeof paymentDetails.payment_data === 'string' 
        ? JSON.parse(paymentDetails.payment_data) 
        : paymentDetails.payment_data;
    } catch {
      return null;
    }
  };

  if (sessionExpired) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content secure-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>🔒 Session Expired</h2>
            <button className="close-btn" onClick={onClose}>&times;</button>
          </div>
          <div className="modal-body">
            <div className="session-expired">
              <div className="expired-icon">⏰</div>
              <h3>Security Session Expired</h3>
              <p>For security reasons, your access to payment details has expired. Please close this window and request access again if needed.</p>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-primary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content secure-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>🔒 Secure Payment Details</h2>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          {step === 'verify' && (
            <div className="verification-step">
              <div className="security-notice">
                <div className="security-icon">🛡️</div>
                <h3>Security Verification Required</h3>
                <p>
                  To protect sensitive payment information, we need to verify your identity. 
                  A verification code will be sent to your registered {user.two_fa_method || 'email'}.
                </p>
              </div>

              <div className="payment-summary">
                <h4>Payment Summary</h4>
                <div className="summary-grid">
                  <div className="summary-item">
                    <label>Client:</label>
                    <span>{payment.first_name} {payment.last_name}</span>
                  </div>
                  <div className="summary-item">
                    <label>Quote:</label>
                    <span>{payment.quote_title}</span>
                  </div>
                  <div className="summary-item">
                    <label>Amount:</label>
                    <span>${parseFloat(payment.amount || 0).toFixed(2)}</span>
                  </div>
                  <div className="summary-item">
                    <label>Date:</label>
                    <span>{formatDate(payment.created_at)}</span>
                  </div>
                </div>
              </div>

              {error && <div className="error-message">{error}</div>}
            </div>
          )}

          {step === 'code' && (
            <div className="code-step">
              <div className="code-notice">
                <div className="code-icon">📱</div>
                <h3>Enter Verification Code</h3>
                <p>
                  We've sent a 6-digit verification code to your registered {user.two_fa_method || 'email'}. 
                  Please enter it below to access payment details.
                </p>
              </div>

              <div className="code-input-section">
                <label>Verification Code:</label>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="code-input"
                  maxLength="6"
                  autoFocus
                />
                <small>Code expires in 5 minutes</small>
              </div>

              {error && <div className="error-message">{error}</div>}
            </div>
          )}

          {step === 'details' && paymentDetails && (
            <div className="details-step">
              <div className="session-timer">
                <div className="timer-icon">⏱️</div>
                <span>Session expires in: {formatTime(timeLeft)}</span>
              </div>

              <div className="payment-details">
                <div className="details-section">
                  <h4>Payment Information</h4>
                  <div className="details-grid">
                    <div className="detail-item">
                      <label>Confirmation Number:</label>
                      <span className="confirmation-number">{paymentDetails.confirmation_number}</span>
                    </div>
                    <div className="detail-item">
                      <label>Transaction ID:</label>
                      <span>{paymentDetails.transaction_id || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Payment Method:</label>
                      <span>{paymentDetails.payment_method || 'Card'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Amount:</label>
                      <span className="amount">${parseFloat(paymentDetails.amount || 0).toFixed(2)}</span>
                    </div>
                    <div className="detail-item">
                      <label>Payment Type:</label>
                      <span className="capitalize">{paymentDetails.payment_type?.replace('_', ' ') || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Status:</label>
                      <span className={`status status-${paymentDetails.status}`}>
                        {paymentDetails.status}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="details-section">
                  <h4>Card Information</h4>
                  <div className="details-grid">
                    {getPaymentData() && (
                      <div>
                        {/* Primary Card */}
                        {getPaymentData().primaryCard && (
                          <div className="card-info">
                            <h5>Primary Card</h5>
                            <div className="detail-item">
                              <label>Card Number:</label>
                              <span className="card-number">
                                {formatCardNumber(getPaymentData().primaryCard.cardNumber)}
                                <SingleCardLogo 
                                  cardType={detectCardType(getPaymentData().primaryCard.cardNumber)} 
                                  size="small" 
                                />
                              </span>
                            </div>
                            <div className="detail-item">
                              <label>Cardholder Name:</label>
                              <span>{getPaymentData().primaryCard.cardName}</span>
                            </div>
                            <div className="detail-item">
                              <label>Expiration Date:</label>
                              <span>{getPaymentData().primaryCard.expirationMonth}/{getPaymentData().primaryCard.expirationYear}</span>
                            </div>
                            <div className="detail-item">
                              <label>CVV:</label>
                              <span>{getPaymentData().primaryCard.cvv}</span>
                            </div>
                            {getPaymentData().primaryCard.billingAddress && (
                              <div className="detail-item full-width">
                                <label>Billing Address:</label>
                                <span>
                                  {getPaymentData().primaryCard.billingAddress.address}, {getPaymentData().primaryCard.billingAddress.city}, {getPaymentData().primaryCard.billingAddress.state} {getPaymentData().primaryCard.billingAddress.zip}
                                </span>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Secondary Card */}
                        {getPaymentData().hasSecondaryPayment && getPaymentData().secondaryCard && (
                          <div className="card-info" style={{marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #eee'}}>
                            <h5>Secondary Card</h5>
                            <div className="detail-item">
                              <label>Card Number:</label>
                              <span className="card-number">
                                {formatCardNumber(getPaymentData().secondaryCard.cardNumber)}
                                <SingleCardLogo 
                                  cardType={detectCardType(getPaymentData().secondaryCard.cardNumber)} 
                                  size="small" 
                                />
                              </span>
                            </div>
                            <div className="detail-item">
                              <label>Cardholder Name:</label>
                              <span>{getPaymentData().secondaryCard.cardName}</span>
                            </div>
                            <div className="detail-item">
                              <label>Expiration Date:</label>
                              <span>{getPaymentData().secondaryCard.expirationMonth}/{getPaymentData().secondaryCard.expirationYear}</span>
                            </div>
                            <div className="detail-item">
                              <label>CVV:</label>
                              <span>{getPaymentData().secondaryCard.cvv}</span>
                            </div>
                            <div className="detail-item">
                              <label>Amount:</label>
                              <span>${parseFloat(getPaymentData().secondaryCard?.amount || 0).toFixed(2)}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="details-section">
                  <h4>Contact Information</h4>
                  <div className="details-grid">
                    {getPaymentData() && getPaymentData().contact && (
                      <div>
                        <div className="detail-item">
                          <label>Email:</label>
                          <span>{getPaymentData().contact.email}</span>
                        </div>
                        <div className="detail-item">
                          <label>Phone:</label>
                          <span>{getPaymentData().contact.phone}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="details-section">
                  <h4>Billing Information</h4>
                  <div className="details-grid">
                    <div className="detail-item full-width">
                      <label>Billing Address:</label>
                      <span>{getBillingAddress(paymentDetails.billing_address)}</span>
                    </div>
                  </div>
                </div>

                <div className="details-section">
                  <h4>Client Information</h4>
                  <div className="details-grid">
                    <div className="detail-item">
                      <label>Client Name:</label>
                      <span>{paymentDetails.first_name} {paymentDetails.last_name}</span>
                    </div>
                    <div className="detail-item">
                      <label>Email:</label>
                      <span>{paymentDetails.email || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Quote:</label>
                      <span>{paymentDetails.quote_title}</span>
                    </div>
                    <div className="detail-item">
                      <label>Quote Number:</label>
                      <span>#{paymentDetails.quote_number}</span>
                    </div>
                  </div>
                </div>

                <div className="details-section">
                  <h4>Timeline</h4>
                  <div className="details-grid">
                    <div className="detail-item">
                      <label>Payment Date:</label>
                      <span>{formatDate(paymentDetails.created_at)}</span>
                    </div>
                    {paymentDetails.processed_at && (
                      <div className="detail-item">
                        <label>Processed Date:</label>
                        <span>{formatDate(paymentDetails.processed_at)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          {step === 'verify' && (
            <>
              <button className="btn btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleRequestVerification}
                disabled={loading}
              >
                {loading ? 'Sending Code...' : 'Send Verification Code'}
              </button>
            </>
          )}

          {step === 'code' && (
            <>
              <button className="btn btn-secondary" onClick={() => setStep('verify')}>
                Back
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleVerifyCode}
                disabled={loading || verificationCode.length !== 6}
              >
                {loading ? 'Verifying...' : 'Verify & View Details'}
              </button>
            </>
          )}

          {step === 'details' && (
            <button className="btn btn-primary" onClick={onClose}>
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SecurePaymentDetailsModal;
